import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getEvalLabelQueueOptions } from '@/api/generated/@tanstack/react-query.gen';
import type { EvalLabelItem } from '@/api/generated/types.gen';
import { upsertReviewSessionAnnotation, type AnnotationTag } from '@/api/reviewStudio';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { pct } from '@/components/Evals/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/evals/label')({
  component: RapidLabelPage,
});

// Number keys 1-9 toggle these while a line is marked wrong.
const TAGS: { id: AnnotationTag; label: string }[] = [
  { id: 'off_script', label: 'Off script' },
  { id: 'wrong_grading', label: 'Wrong grading' },
  { id: 'missed_learner', label: 'Ignored learner' },
  { id: 'repetition', label: 'Repetition' },
  { id: 'too_long', label: 'Too long / filler' },
  { id: 'hallucination', label: 'Wrong / invented' },
  { id: 'asked_before_taught', label: 'Asked before taught' },
  { id: 'card_timing', label: 'Card timing' },
  { id: 'missing_card', label: 'Missing card' },
];

type Phase = 'label' | 'reveal';

function RapidLabelPage() {
  const queryClient = useQueryClient();
  const queue = useQuery({ ...getEvalLabelQueueOptions({ query: { limit: 15 } }), refetchOnWindowFocus: false });
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('label');
  const [verdict, setVerdict] = useState<'good' | 'bad' | null>(null);
  const [tags, setTags] = useState<AnnotationTag[]>([]);
  const [expected, setExpected] = useState('');
  const [why, setWhy] = useState('');
  const [saving, setSaving] = useState(false);
  const [session, setSession] = useState({ labeled: 0, agree: 0 });
  const [last, setLast] = useState<{ item: EvalLabelItem; verdict: 'good' | 'bad' } | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const items = queue.data?.items ?? [];
  const item = items[index];
  const totalLabeled = (queue.data?.labeled ?? 0) + session.labeled;
  const totalAgree = (queue.data?.agree ?? 0) + session.agree;

  const reset = () => {
    setPhase('label');
    setVerdict(null);
    setTags([]);
    setExpected('');
    setWhy('');
  };

  const next = () => {
    reset();
    if (index + 1 >= items.length) {
      setIndex(0);
      queryClient.invalidateQueries({ queryKey: getEvalLabelQueueOptions({ query: { limit: 15 } }).queryKey });
    } else {
      setIndex(index + 1);
    }
  };

  const save = async (notes = '') => {
    if (!item || !verdict) return;
    setSaving(true);
    try {
      await upsertReviewSessionAnnotation(item.room_id, {
        target: 'transcript',
        target_id: item.target_id,
        taste: 3,
        quality: 3,
        hallucination: 3,
        notes,
        is_bug: false,
        tags: verdict === 'bad' ? tags : [],
        verdict,
        expected_text: verdict === 'bad' ? expected : '',
      });
      return true;
    } catch {
      toast.error("Couldn't save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!item || !verdict || saving) return;
    if (phase === 'label') {
      if (!(await save())) return;
      const agrees = verdict === item.judge_label;
      setSession((s) => ({ labeled: s.labeled + 1, agree: s.agree + (agrees ? 1 : 0) }));
      setLast({ item, verdict });
      setPhase('reveal');
      // When you agree there is nothing to add: move on by itself.
      if (agrees) window.setTimeout(next, 900);
      else window.setTimeout(() => textRef.current?.focus(), 50);
      return;
    }
    // Disagreement: your reason becomes a few-shot example for the next judge version.
    if (why.trim()) await save(why.trim());
    next();
  };

  const undo = async () => {
    if (!last) return;
    toast('Back to the last line');
    setSession((s) => ({ labeled: Math.max(0, s.labeled - 1), agree: s.agree - (last.verdict === last.item.judge_label ? 1 : 0) }));
    const at = items.findIndex((i) => i.target_id === last.item.target_id);
    if (at >= 0) setIndex(at);
    reset();
    setVerdict(last.verdict);
    setLast(null);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.tagName === 'TEXTAREA';
      if (e.key === 'Enter' && (!typing || e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        submit();
        return;
      }
      if (typing) {
        if (e.key === 'Escape') (e.target as HTMLElement).blur();
        return;
      }
      if (phase !== 'label') return;
      const k = e.key.toLowerCase();
      if (k === 'g') setVerdict('good');
      else if (k === 'b') setVerdict('bad');
      else if (k === 's') next();
      else if (k === 'u') undo();
      else if (k === 'e' && verdict === 'bad') {
        e.preventDefault();
        textRef.current?.focus();
      } else if (/^[1-9]$/.test(k) && verdict === 'bad') {
        const tag = TAGS[Number(k) - 1]?.id;
        if (tag) setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <EvalShell
      title="Rapid labeling"
      subtitle="Label blind, then see the judge's verdict."
      actions={
        <div className="flex items-center gap-3 text-[12px]">
          <span className="rounded-full bg-neutral-100 px-3 py-1.5 text-neutral-700">
            Agreement with the judge{' '}
            <span className="font-semibold">{totalLabeled ? pct(totalAgree / totalLabeled) : '–'}</span>
            <span className="text-neutral-400">
              {' '}
              ({totalAgree}/{totalLabeled})
            </span>
          </span>
          <span className="text-neutral-500">{Math.max(0, (queue.data?.remaining ?? 0) - session.labeled)} left</span>
        </div>
      }
    >
      {!queue.data ? (
        <Skeleton className="h-96 rounded-3xl" />
      ) : !item ? (
        <Panel>
          <p className="text-sm text-neutral-500">
            Nothing left to label. Run an experiment, or check the{' '}
            <Link to="/evals/evaluators" className="font-medium text-neutral-800 hover:underline">
              judge's alignment
            </Link>
            .
          </p>
        </Panel>
      ) : (
        <div className="mx-auto max-w-3xl space-y-3">
          <p className="text-[11px] uppercase tracking-wide text-neutral-400">
            {item.fixture_key} · {item.persona} learner{item.step ? ` · ${item.step}` : ''}
          </p>
          <Panel>
            <div className="space-y-1.5">
              {(item.history ?? []).map((h, i) => (
                <p key={i} className="text-[13px] leading-relaxed text-neutral-400">
                  <span className="mr-2 text-[10px] font-semibold uppercase">{h.who === 'tutor' ? 'Tutor' : 'Learner'}</span>
                  {h.text}
                </p>
              ))}
            </div>
            {(item.directive || item.screen) && (
              <div className="mt-3 space-y-1 rounded-2xl bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                {item.directive && (
                  <p>
                    <span className="font-semibold">Told: </span>
                    {item.directive.replace('[LESSON STEP — do exactly this, then stop and wait for the learner]', '').trim()}
                  </p>
                )}
                {item.screen && (
                  <p>
                    <span className="font-semibold">Screen: </span>
                    {item.screen}
                  </p>
                )}
              </div>
            )}
            <p
              className={cn(
                'mt-4 rounded-2xl px-4 py-3 text-[17px] font-medium leading-relaxed text-neutral-900 ring-2',
                verdict === 'good' ? 'ring-emerald-400' : verdict === 'bad' ? 'ring-rose-400' : 'ring-neutral-200',
              )}
            >
              <span className="mr-2 text-[10px] font-semibold uppercase text-neutral-400">Tutor</span>
              {item.text}
            </p>
            {item.learner_next && (
              <p className="mt-3 text-[13px] text-neutral-400">
                <span className="mr-2 text-[10px] font-semibold uppercase">Learner then</span>
                {item.learner_next}
              </p>
            )}
          </Panel>

          {phase === 'label' ? (
            <Panel>
              <div className="flex flex-wrap items-center gap-2">
                {(['good', 'bad'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVerdict(v)}
                    className={cn(
                      'h-9 rounded-full px-4 text-[13px] font-semibold',
                      verdict === v
                        ? v === 'good'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-rose-600 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                    )}
                  >
                    {v === 'good' ? 'Right' : 'Wrong'}
                    <kbd className="ml-2 text-[10px] opacity-60">{v === 'good' ? 'G' : 'B'}</kbd>
                  </button>
                ))}
                <span className="ml-auto text-[11px] text-neutral-400">
                  Enter save · S skip · U undo{verdict === 'bad' ? ' · 1–9 tags · E should have said' : ''}
                </span>
              </div>
              {verdict === 'bad' && (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap gap-1">
                    {TAGS.map((t, i) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTags((prev) => (prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]))}
                        className={cn(
                          'h-7 rounded-full px-2.5 text-[11px] font-medium',
                          tags.includes(t.id) ? 'bg-[#AAE73D] text-neutral-900' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200',
                        )}
                      >
                        <span className="mr-1 opacity-50">{i + 1}</span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <Textarea
                    ref={textRef}
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                    placeholder="What it should have said (optional) · Esc to leave, ⌘Enter to save"
                    className="min-h-16 text-[13px]"
                  />
                </div>
              )}
            </Panel>
          ) : (
            <Panel
              className={cn(
                verdict === item.judge_label ? 'bg-emerald-50 ring-emerald-200' : 'bg-rose-50 ring-rose-200',
              )}
            >
              <p className="text-[13px] font-semibold text-neutral-900">
                {verdict === item.judge_label
                  ? `The judge agrees: ${item.judge_label}`
                  : `The judge said ${item.judge_label}, you said ${verdict}`}
              </p>
              <p className="mt-1 text-[12px] text-neutral-600">{item.judge_explanation}</p>
              {verdict !== item.judge_label && (
                <Textarea
                  ref={textRef}
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  placeholder="Why is the judge wrong here? One line. It becomes an example for the next judge version. ⌘Enter to continue"
                  className="mt-2 min-h-14 bg-white text-[13px]"
                />
              )}
            </Panel>
          )}
        </div>
      )}
    </EvalShell>
  );
}
